// The places stream's look on the streamed valley (phase C joined): what each chunk draws of the kit and the water, and
// the rooms the hero walks into.
//   - the kit: a chunk's kit placements batched into one mesh (`batchPieces`, owners unique across the valley so one
//     cut-away names one house) on the kit surface (night-lit faces, the dithered cut-away); the props merge into the
//     chunk's surface mesh;
//   - the water: the fitted shapes' sheet over each chunk's square (the same grid on both sides of a chunk edge, so the
//     sheets meet), on the low-poly water material, its waves on real time;
//   - the rooms: when the interiors director (in the sim) says the hero is inside, the camera asks for the room's
//     profile and looks in over the cut roof and walls (interior-view.ts), and the room's light (the sky's indoor look
//     for the room's profile) blends in over the places look's `lightBlend` seconds; the kit's lit windows and lamps
//     follow the outdoor night (the day look's lamps). Nothing loads: the room was always there.
// A view: it reads the sim and never writes it.
import * as THREE from "three";
import { kitSurface, kitUniforms, lowPolyWater, setKitNight, waterUniforms, type KitUniforms, type LowPolyLook, type WaterUniforms } from "@rpg/look";
import { batchPieces, waterSurface, type KitTier } from "@rpg/meshkit";
import { waterChannel, waterLevel, waterShapeDistance, type WaterVolume } from "@rpg/collision";
import type { Runtime } from "@rpg/runtime";
import type { Area } from "@rpg/world";
import type { ValleyCamera } from "../valley/camera";
import { interiorCamera, interiorCut } from "../places/interior-view";
import { buildProp, isProp } from "./props";
import type { WorldMount } from "./scene";
import type { WorldRoom } from "./places";

/** What the sky's view reads from the rooms each frame: the room's light profile and how far it has blended in. */
export interface RoomLight {
  indoor: string | null;
  mix: number;
}

export interface WorldPlacesView {
  kit: { material: THREE.Material; uniforms: KitUniforms };
  water: { material: THREE.Material; uniforms: WaterUniforms };
  /** A chunk's kit placements as one mesh, or null when it has none. */
  kitMesh(chunk: string): THREE.Mesh | null;
  /** A chunk's props, faceted on the surface's palette (to merge into the chunk's mesh), or null. */
  props(chunk: string): THREE.BufferGeometry | null;
  /** The water over a chunk's square, or null when none reaches it. */
  waterMesh(area: Area): THREE.Mesh | null;
  /** Build every kit piece at `tier` and every chunk's water sheet now (the boot's warm-up). */
  prebuild(tier: KitTier): void;
  /** Add one tiny mesh of each material (kit, water) for the look's warm-up; the function removes them. */
  warm(scene: THREE.Scene): () => void;
  /** The room light for the sky's view. */
  light(): RoomLight;
  state(): { inside: string | null; light: string | null; mix: number; cut: { owner: number; mask: number; fade: number }; night: number; cameraSolid: boolean };
}

const ALONG = new Set(["building", "prop", "interactable", "camp"]);

export function mountWorldPlacesView(o: {
  runtime: Runtime;
  m: WorldMount;
  look: LowPolyLook;
  tier: KitTier;
  camera: ValleyCamera | null;
  /** The outdoor night (0 day .. 1 night: the day look's lamps); the kit's lit faces follow it. */
  night: () => number;
}): WorldPlacesView {
  const { m, look } = o;
  const pc = m.kit.content;
  const ku = kitUniforms(pc.look.litColour);
  const kitMat = kitSurface(look.uniforms, ku);
  kitMat.userData.resident = true;
  const wl = pc.look.water;
  const wu = waterUniforms({ height: wl.height, length: wl.length, speed: wl.speed, calmShore: wl.calmShore, foam: wl.foam, foamWidth: wl.foamWidth, foamPulse: wl.foamPulse, directions: [0.4, 2.1, -1.2] });
  const waterMat = lowPolyWater(look.uniforms, wu);
  waterMat.userData.resident = true;
  const colours = { shallow: pc.palette(wl.shallow), deep: pc.palette(wl.deep), edge: pc.palette(wl.edge) };
  const ground = (x: number, z: number): number => m.world.heightAt(x, z);

  const kitMesh = (chunk: string): THREE.Mesh | null => {
    const items = m.world
      .placementsIn(chunk)
      .filter((p) => ALONG.has(p.kind) && m.kit.isKit(p.piece))
      .map((p) => ({ piece: m.kit.piece(p.piece, p.seed, o.tier), place: { x: p.pos[0], y: p.y, z: p.pos[1], yaw: p.yaw }, owner: m.owners.get(p.id)! }));
    if (!items.length) return null;
    const mesh = new THREE.Mesh(batchPieces(items).geometry, kitMat);
    mesh.name = `kit:${chunk}`;
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    return mesh;
  };
  const props = (chunk: string): THREE.BufferGeometry | null => {
    const parts: THREE.BufferGeometry[] = [];
    const mat = new THREE.Matrix4();
    for (const p of m.world.placementsIn(chunk)) {
      if (!ALONG.has(p.kind) || !isProp(p.piece)) continue;
      const g = buildProp(p.piece, p.seed, pc.palette).geometry;
      if (!g) continue;
      g.applyMatrix4(mat.makeRotationY(p.yaw).setPosition(p.pos[0], p.y, p.pos[1]));
      parts.push(g);
    }
    if (!parts.length) return null;
    if (parts.length === 1) return parts[0]!;
    // the props share position, normal, color and glow: concatenate
    const out = new THREE.BufferGeometry();
    for (const name of ["position", "normal", "color", "glow"]) {
      const size = parts[0]!.getAttribute(name).itemSize;
      const arr = new Float32Array(parts.reduce((n, g) => n + g.getAttribute(name).count, 0) * size);
      let off = 0;
      for (const g of parts) {
        arr.set(g.getAttribute(name).array as Float32Array, off);
        off += g.getAttribute(name).count * size;
      }
      out.setAttribute(name, new THREE.BufferAttribute(arr, size));
    }
    for (const g of parts) g.dispose();
    return out;
  };
  const near = (v: WaterVolume, a: Area): boolean => v.max.x + wl.margin + 1 >= a.x0 && v.min.x - wl.margin - 1 <= a.x1 && v.max.z + wl.margin + 1 >= a.z0 && v.min.z - wl.margin - 1 <= a.z1;
  /** A stream's channel cut down to the stretch near a chunk (every point that can be the nearest for a vertex in it,
   *  and one more each side), so its sheet's distance and level queries walk a few points, not the whole course; the
   *  same answers inside the square. Null when the stream passes nowhere near. */
  const localWater = (v: WaterVolume, a: Area): WaterVolume | null => {
    if (v.shape?.type !== "channel") return v;
    const pts = v.shape.points;
    const reach = Math.max(...pts.map((p) => p.halfWidth)) + wl.margin + 4;
    let lo = -1;
    let hi = -1;
    pts.forEach((p, k) => {
      if (p.x >= a.x0 - reach && p.x <= a.x1 + reach && p.z >= a.z0 - reach && p.z <= a.z1 + reach) {
        if (lo < 0) lo = k;
        hi = k;
      }
    });
    if (lo < 0) return null;
    const run = pts.slice(Math.max(0, lo - 1), Math.min(pts.length, hi + 2));
    return run.length >= 2 ? waterChannel(v.id, run, v.max.y - v.min.y) : null;
  };
  /** The water sheets over a chunk's square (built once: a sheet near the pond's or a fast stretch's shore is several
   *  ms of queries, so they are built at the warm-up and the streamer's step only copies one). */
  const waterGeometry = (a: Area): THREE.BufferGeometry | null => {
    const sheets: THREE.BufferGeometry[] = [];
    m.water.forEach((whole, i) => {
      if (!near(whole, a)) return;
      const v = localWater(whole, a);
      if (!v) return;
      // the chunk's whole square: both sides of an edge put their vertices at the same points along it
      const g = waterSurface({
        min: { x: a.x0, z: a.z0 },
        max: { x: a.x1, z: a.z1 },
        cell: wl.cell,
        distance: (x, z) => waterShapeDistance(v, x, z),
        level: (x, z) => waterLevel(v, x, z),
        floor: ground,
        colours,
        deepAt: wl.deepAt,
        edgeWidth: wl.edgeWidth,
        margin: wl.margin,
        seed: 0x3a7e + i * 977 + Math.round(a.x0) * 31 + Math.round(a.z0),
      });
      if (g.getAttribute("position").count) sheets.push(g);
      else g.dispose();
    });
    if (!sheets.length) return null;
    let geo = sheets[0]!;
    if (sheets.length > 1) {
      geo = new THREE.BufferGeometry();
      for (const name of Object.keys(sheets[0]!.attributes)) {
        const size = sheets[0]!.getAttribute(name).itemSize;
        const arr = new Float32Array(sheets.reduce((n, g) => n + g.getAttribute(name).count, 0) * size);
        let off = 0;
        for (const g of sheets) {
          arr.set(g.getAttribute(name).array as Float32Array, off);
          off += g.getAttribute(name).count * size;
        }
        geo.setAttribute(name, new THREE.BufferAttribute(arr, size));
      }
      for (const g of sheets) g.dispose();
      geo.computeBoundingSphere();
    }
    return geo;
  };
  const waterCache = new Map<string, THREE.BufferGeometry | null>();
  const keyOf = (a: Area): string => `${a.x0},${a.z0}`;
  const waterMesh = (a: Area): THREE.Mesh | null => {
    const k = keyOf(a);
    if (!waterCache.has(k)) waterCache.set(k, waterGeometry(a));
    const cached = waterCache.get(k);
    if (!cached) return null;
    // a copy: the streamer disposes a chunk's geometry when the chunk goes
    const mesh = new THREE.Mesh(cached.clone(), waterMat);
    mesh.name = "water";
    mesh.castShadow = false;
    mesh.receiveShadow = true;
    return mesh;
  };

  // the rooms: the camera, the cut-away and the light
  const roomOf = new Map<string, WorldRoom>(m.rooms.map((r) => [r.id, r]));
  const camera = interiorCamera(o.camera);
  const cut = interiorCut({ kit: ku, fade: pc.look.cutFade, margin: pc.look.cutMargin });
  let inside: string | null = null;
  let lightId: string | null = null;
  let mix = 0;
  let wTime = 0;
  o.runtime.on(
    "late",
    (f) => {
      if (f.mode === "play" && o.runtime.paused) return;
      const s = m.places().state().interiors;
      if (s.inside !== inside) {
        if (inside) camera.exit();
        inside = s.inside;
        const room = inside ? roomOf.get(inside) : undefined;
        if (room) {
          camera.enter(room.spec.profile);
          // a room straight after another starts its own light from nothing
          if (lightId !== room.spec.light) mix = 0;
          lightId = room.spec.light;
        }
      }
      const dt = f.realDt;
      const room = inside ? (roomOf.get(inside) ?? null) : null;
      const eye = o.camera ? o.camera.rig.frame().position : { x: 0, z: 0 };
      cut.frame(room ? { owner: room.owner, interior: room.piece.interior!, place: room.place } : null, eye, dt);
      const blend = Math.max(1e-3, pc.look.lightBlend);
      mix = room ? Math.min(1, mix + dt / blend) : Math.max(0, mix - dt / blend);
      if (!room && mix === 0) lightId = null;
      setKitNight(ku, o.night());
      wTime += dt;
      wu.uWaterTime.value = wTime;
    },
    { order: -5, name: "world places: rooms and water" },
  );

  const tiny = (mat: THREE.Material, attrs: Record<string, number>): THREE.Mesh => {
    const g = new THREE.BufferGeometry();
    g.setAttribute("position", new THREE.Float32BufferAttribute([0, 0, 0, 0.01, 0, 0, 0, 0, 0.01], 3));
    g.setAttribute("normal", new THREE.Float32BufferAttribute([0, 1, 0, 0, 1, 0, 0, 1, 0], 3));
    g.setAttribute("color", new THREE.Float32BufferAttribute([1, 1, 1, 1, 1, 1, 1, 1, 1], 3));
    for (const [name, size] of Object.entries(attrs)) g.setAttribute(name, new THREE.Float32BufferAttribute(new Float32Array(3 * size), size));
    const mesh = new THREE.Mesh(g, mat);
    mesh.position.set(0, -500, 0);
    mesh.frustumCulled = false;
    mesh.castShadow = mat === kitMat;
    mesh.receiveShadow = true;
    return mesh;
  };

  return {
    kit: { material: kitMat, uniforms: ku },
    water: { material: waterMat, uniforms: wu },
    kitMesh,
    props,
    waterMesh,
    prebuild(tier) {
      // every kit piece at this tier and every chunk's water, before ready (a first build of a house is tens of ms)
      for (const p of m.world.placements()) if (ALONG.has(p.kind) && m.kit.isKit(p.piece)) m.kit.piece(p.piece, p.seed, tier);
      for (const c of m.world.chunks) {
        const k = keyOf(c.area);
        if (!waterCache.has(k)) waterCache.set(k, waterGeometry(c.area));
      }
    },
    warm(scene) {
      const meshes = [tiny(kitMat, { glow: 1, lit: 1, cut: 1 }), tiny(waterMat, { glow: 1, shore: 1 })];
      scene.add(...meshes);
      return () => {
        for (const x of meshes) {
          x.removeFromParent();
          x.geometry.dispose();
        }
      };
    },
    light: () => ({ indoor: mix > 0 ? lightId : null, mix }),
    state: () => ({ inside, light: lightId, mix, cut: cut.state(), night: ku.uKitNight.value, cameraSolid: camera.solid() }),
  };
}
